import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Button from 'common-ui/button';
import Pagination from 'common-ui/table/Pagination';
import {
  assignPrivilege,
  createUserPrivilege,
  deleteUserPrivilege,
  getUserPrivileges,
  updateUserPrivilege,
} from 'features/newsales/newsalesActions';
import {
  selectUserPrivileges,
  selectUserPrivilegesCurrentPage,
  selectUserPrivilegesDeleteSuccess,
  selectUserPrivilegesLoading,
  selectUserPrivilegesPagesCount,
  selectUserPrivilegesSearchData,
  selectUserPrivilegesSuccess,
  selectUserPrivilegesUpdateSuccess,
  setUserPrivilegesCreateSuccess,
  setUserPrivilegesCurrentPage,
  setUserPrivilegesDeleteSuccess,
  setUserPrivilegesSearchData,
  setUserPrivilegesUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import AssignPrivilegesModal from '../components/AssignPrivilegesModal/AssignPrivilegesModal';
import SearchFilter from '../components/SearchFilter';
import UserPrivilegeCard from '../components/UserPrivilegeCard';
import UserPrivilegeModal from '../components/UserPrivilegeModal';
import {
  EmptyState,
  LoadingSpinner,
  PaginationWrapper,
  PrivilegeList,
  PrivilegesContainer,
  PrivilegesContent,
  PrivilegesTitle,
} from './Privileges.styles';

const Privileges = () => {
  const dispatch = useDispatch();

  const userPrivileges = useSelector(selectUserPrivileges);
  const loading = useSelector(selectUserPrivilegesLoading);
  const currentPage = useSelector(selectUserPrivilegesCurrentPage);
  const pagesCount = useSelector(selectUserPrivilegesPagesCount);
  const searchData = useSelector(selectUserPrivilegesSearchData);
  const createSuccess = useSelector(selectUserPrivilegesSuccess);
  const deleteSuccess = useSelector(selectUserPrivilegesDeleteSuccess);
  const updateSuccess = useSelector(selectUserPrivilegesUpdateSuccess);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  useEffect(() => {
    const params = {
      status: searchData.status,
      page: currentPage,
      limit: searchData.limit,
      search: searchData.search,
    };
    dispatch(getUserPrivileges(params));
  }, [dispatch, currentPage, searchData.status, searchData.limit, searchData.search]);

  useEffect(() => {
    if (createSuccess) {
      setIsModalOpen(false);
      setSelectedPrivilege(null);
      dispatch(setUserPrivilegesCreateSuccess(false));
      const params = {
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        search: searchData.search,
      };
      dispatch(getUserPrivileges(params));
    }
  }, [createSuccess, dispatch, currentPage, searchData]);

  useEffect(() => {
    if (updateSuccess) {
      setIsModalOpen(false);
      setSelectedPrivilege(null);
      dispatch(setUserPrivilegesUpdateSuccess(false));
      const params = {
        status: searchData.status,
        page: currentPage,
        limit: searchData.limit,
        search: searchData.search,
      };
      dispatch(getUserPrivileges(params));
    }
  }, [updateSuccess, dispatch, currentPage, searchData]);

  useEffect(() => {
    if (deleteSuccess) {
      dispatch(setUserPrivilegesDeleteSuccess(false));

      // If deleting the last item on a page (and not on page 1), go to previous page
      const shouldGoToPreviousPage = userPrivileges?.length === 1 && currentPage > 1;

      if (shouldGoToPreviousPage) {
        dispatch(setUserPrivilegesCurrentPage(currentPage - 1));
      } else {
        // Refetch current page
        const params = {
          status: searchData.status,
          page: currentPage,
          limit: searchData.limit,
          search: searchData.search,
        };
        dispatch(getUserPrivileges(params));
      }
    }
  }, [deleteSuccess, dispatch, userPrivileges?.length, currentPage, searchData]);

  const handleSearchChange = (value) => {
    dispatch(setUserPrivilegesSearchData({ search: value }));
    dispatch(setUserPrivilegesCurrentPage(1));
  };

  const handleStatusChange = (value) => {
    dispatch(setUserPrivilegesSearchData({ status: value }));
    dispatch(setUserPrivilegesCurrentPage(1));
  };

  const handlePageChange = (page) => {
    dispatch(setUserPrivilegesCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setUserPrivilegesSearchData({ limit: count }));
    dispatch(setUserPrivilegesCurrentPage(1));
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedPrivilege(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (id) => {
    const privilegeToEdit = userPrivileges?.find((privilege) => privilege.id === id);
    if (privilegeToEdit) {
      setModalMode('edit');
      setSelectedPrivilege(privilegeToEdit);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPrivilege(null);
  };

  const handleSubmit = (idOrData, privilegeData) => {
    if (modalMode === 'create') {
      dispatch(createUserPrivilege(idOrData));
    } else {
      dispatch(updateUserPrivilege({ id: idOrData, data: privilegeData }));
    }
  };

  const handleDeletePrivilege = (id) => {
    dispatch(deleteUserPrivilege(id));
  };

  const handleOpenAssignModal = () => {
    setIsAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
  };

  const handleAssignPrivileges = async (data) => {
    const { privilegeId, assignmentType, selectedItems } = data;

    const bulkData = {
      userPrivilegeId: privilegeId,
    };

    if (assignmentType === 'user') {
      bulkData.targetUsers = selectedItems.map((item) => ({
        uuid: item.value,
        name: item.label,
        email: item.email,
        userType: 'user',
      }));
    } else if (assignmentType === 'team') {
      bulkData.targetTeamIds = selectedItems.map((item) => item.value);
    } else if (assignmentType === 'department') {
      bulkData.targetDepartmentIds = selectedItems.map((item) => item.value);
    } else if (assignmentType === 'branch') {
      bulkData.targetBranchIds = selectedItems.map((item) => item.value);
    }

    try {
      await dispatch(assignPrivilege(bulkData));
      setIsAssignModalOpen(false);
    } catch (error) {
      console.error('Error assigning privileges:', error);
    }
  };

  return (
    <PrivilegesContainer>
      <PrivilegesTitle>User Privileges</PrivilegesTitle>
      <SearchFilter
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
        searchPlaceholder="Search user privileges..."
        showCreateButton
        onCreateClick={handleOpenCreateModal}
        createButtonText="+ Create User Privilege"
        extraButton={
          <Button
            outlined
            onClick={handleOpenAssignModal}
            style={{ minWidth: '160px', width: 'auto' }}
          >
            Assignee Privileges
          </Button>
        }
      />
      <PrivilegesContent>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : !userPrivileges || userPrivileges.length === 0 ? (
          <EmptyState>No user privileges found</EmptyState>
        ) : (
          <>
            <PrivilegeList>
              {userPrivileges?.map((privilege) => (
                <UserPrivilegeCard
                  key={privilege.id}
                  name={privilege.name}
                  description={privilege.description}
                  viewableStatuses={privilege.viewableStatuses || []}
                  changeableStatuses={privilege.changeableStatuses || []}
                  enabled={privilege.enabled}
                  onEdit={() => handleOpenEditModal(privilege.id)}
                  onDelete={() => handleDeletePrivilege(privilege.id)}
                />
              ))}
            </PrivilegeList>

            {pagesCount > 1 && (
              <PaginationWrapper>
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagesCount}
                  onPageChange={handlePageChange}
                  handleRowCountChange={handleRowCountChange}
                  dataCount={userPrivileges?.length || 0}
                  count={searchData.limit}
                  editableRowCount
                />
              </PaginationWrapper>
            )}
          </>
        )}
      </PrivilegesContent>

      <UserPrivilegeModal
        mode={modalMode}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCreate={handleSubmit}
        onUpdate={handleSubmit}
        initialData={selectedPrivilege}
      />

      <AssignPrivilegesModal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        onAssign={handleAssignPrivileges}
        privileges={userPrivileges || []}
      />
    </PrivilegesContainer>
  );
};

export default Privileges;
